import { useState, useMemo } from 'react';
import type { AnalyticalTimeseriesResponse } from '@/api/dataVault';
import { WellSelectorPanel } from './WellSelectorPanel';
import { EmptyState } from './ui/EmptyState';
import { WELL_COLORS } from './chartTheme';
import { useCrossFilter } from './CrossFilterContext';
import { defaultHiddenWellIds, defaultSelectedAnalyteCode } from './chartDefaults';

interface Props {
  data: AnalyticalTimeseriesResponse;
}

/** Continuous color gradient: green (ratio=0) -> yellow (ratio=1) -> red (ratio>=2) */
function ratioToColor(ratio: number): string {
  const clamped = Math.min(ratio, 3);
  if (clamped <= 1) {
    // Green to yellow: hue 120 -> 60
    const hue = 120 - clamped * 60;
    return `hsl(${hue}, 65%, 30%)`;
  }
  // Yellow to red: hue 60 -> 0
  const t = Math.min((clamped - 1) / 1.5, 1);
  const hue = 60 - t * 60;
  return `hsl(${hue}, 70%, 32%)`;
}

export function ExceedanceHeatmap({ data }: Props) {
  // Exceedance is a wells × dates matrix — default to all wells visible.
  // Analyte stays single (Benzene by default).
  const [hiddenWellIds, setHiddenWellIds] = useState<Set<string>>(new Set());
  const [selectedAnalyte, setSelectedAnalyte] = useState<string>(() => defaultSelectedAnalyteCode(data));
  const { highlight, getOpacity } = useCrossFilter();

  if (!data.wells.length) {
    return <EmptyState message="NO ANALYTICAL DATA AVAILABLE" />;
  }

  const allAnalytes = useMemo(() => {
    const map = new Map<string, string>();
    for (const well of data.wells) {
      for (const [code, analyte] of Object.entries(well.analytes)) {
        if (!map.has(code)) map.set(code, analyte.compound);
      }
    }
    return Array.from(map.entries())
      .map(([code, compound]) => ({ code, compound }))
      .sort((a, b) => a.compound.toLowerCase().localeCompare(b.compound.toLowerCase()));
  }, [data.wells]);

  const activeAnalyte = selectedAnalyte || (allAnalytes.find(a => a.compound.toLowerCase() === 'benzene')?.code ?? allAnalytes[0]?.code ?? '');

  const visibleWells = data.wells.filter(w => !hiddenWellIds.has(w.well_id));

  const hasRegLimits = useMemo(() => {
    for (const well of data.wells) {
      const analyte = well.analytes[activeAnalyte];
      if (analyte?.regulatory_limits && Object.keys(analyte.regulatory_limits).length > 0) {
        return true;
      }
    }
    return false;
  }, [data.wells, activeAnalyte]);

  // Get the primary MCL/limit value for gradient computation
  const primaryLimit = useMemo(() => {
    for (const well of data.wells) {
      const analyte = well.analytes[activeAnalyte];
      if (analyte?.regulatory_limits) {
        const values = Object.values(analyte.regulatory_limits).map(l => l.value);
        if (values.length > 0) return Math.min(...values);
      }
      if (analyte?.mcl !== null && analyte?.mcl !== undefined) return analyte.mcl;
    }
    return null;
  }, [data.wells, activeAnalyte]);

  const { dates, grid } = useMemo(() => {
    const dateSet = new Set<string>();
    for (const well of visibleWells) {
      const analyte = well.analytes[activeAnalyte];
      if (analyte) {
        for (const m of analyte.measurements) {
          dateSet.add(m.date);
        }
      }
    }
    const sortedDates = Array.from(dateSet).sort();

    const gridData = visibleWells.map(well => {
      const analyte = well.analytes[activeAnalyte];
      // Pre-index measurements by date for O(1) lookup instead of O(n) .find()
      const measurementsByDate = new Map<string, typeof analyte extends undefined ? never : NonNullable<typeof analyte>['measurements'][0]>();
      if (analyte) {
        for (const m of analyte.measurements) {
          measurementsByDate.set(m.date, m);
        }
      }
      const cells = sortedDates.map(date => {
        if (!analyte) return { status: 'empty' as const, value: null as number | null, ratio: null as number | null };
        const m = measurementsByDate.get(date);
        if (!m) return { status: 'empty' as const, value: null, ratio: null };
        if (m.is_non_detect) return { status: 'nd' as const, value: m.concentration, ratio: null };
        if (m.concentration === null) return { status: 'no_mcl' as const, value: m.concentration, ratio: null };

        const ratio = primaryLimit ? m.concentration / primaryLimit : null;

        const limits = analyte.regulatory_limits;
        if (limits && Object.keys(limits).length > 0) {
          const limitValues = Object.values(limits).map(l => l.value);
          const exceedsCount = limitValues.filter(v => m.concentration! > v).length;
          if (exceedsCount === limitValues.length) return { status: 'exceeds_all' as const, value: m.concentration, ratio };
          if (exceedsCount > 0) return { status: 'exceeds_some' as const, value: m.concentration, ratio };
          return { status: 'below' as const, value: m.concentration, ratio };
        }

        const mcl = analyte.mcl;
        if (mcl !== null && m.concentration > mcl) return { status: 'exceeds' as const, value: m.concentration, ratio };
        if (mcl !== null) return { status: 'below' as const, value: m.concentration, ratio };
        return { status: 'no_mcl' as const, value: m.concentration, ratio };
      });
      return { well_name: well.well_name, cells };
    });

    return { dates: sortedDates, grid: gridData };
  }, [visibleWells, activeAnalyte, primaryLimit]);

  const wellItems = data.wells.map((w, i) => ({
    well_id: w.well_id,
    well_name: w.well_name,
    color: WELL_COLORS[i % WELL_COLORS.length],
  }));

  const visibleWellIdSet = new Set(visibleWells.map(w => w.well_id));

  function getCellStyle(cell: { status: string; ratio: number | null }): { className: string; style?: React.CSSProperties } {
    if (cell.ratio !== null && primaryLimit) {
      // Use continuous gradient
      return {
        className: 'w-full h-5 rounded-sm border border-white/5',
        style: { backgroundColor: ratioToColor(cell.ratio) },
      };
    }
    // Fallback to categorical
    const classMap: Record<string, string> = {
      exceeds_all: 'w-full h-5 rounded-sm border bg-red-900/60 border-red-700/40',
      exceeds: 'w-full h-5 rounded-sm border bg-red-900/60 border-red-700/40',
      exceeds_some: 'w-full h-5 rounded-sm border bg-orange-900/50 border-orange-700/40',
      below: 'w-full h-5 rounded-sm border bg-emerald-900/50 border-emerald-700/40',
      nd: 'w-full h-5 rounded-sm border bg-mono-800/60 border-mono-700/40',
      no_mcl: 'w-full h-5 rounded-sm border bg-blue-900/30 border-blue-800/30',
      empty: 'w-full h-5 rounded-sm border bg-mono-900/20 border-mono-800/20',
    };
    return { className: classMap[cell.status] || classMap.empty };
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <span className="text-[10px] text-mono-500 tracking-wider">ANALYTE:</span>
        <div className="flex gap-1 flex-wrap">
          {allAnalytes.map(a => (
            <button
              key={a.code}
              onClick={() => setSelectedAnalyte(a.code)}
              className={`px-2.5 py-1 text-[10px] tracking-wider rounded transition-colors ${
                activeAnalyte === a.code
                  ? 'bg-mono-700 text-mono-100'
                  : 'text-mono-500 hover:text-mono-300 hover:bg-mono-800'
              }`}
            >
              {a.compound}
            </button>
          ))}
        </div>
      </div>

      <WellSelectorPanel
        wells={wellItems}
        visibleWellIds={visibleWellIdSet}
        onToggle={(id) => {
          setHiddenWellIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
          });
        }}
        onSelectAll={() => setHiddenWellIds(new Set())}
        onDeselectAll={() => setHiddenWellIds(new Set(data.wells.map(w => w.well_id)))}
      />

      {/* Legend */}
      <div className="flex items-center gap-4 text-[9px] tracking-wider text-mono-400">
        {primaryLimit ? (
          <>
            <div className="flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded" style={{ backgroundColor: ratioToColor(0) }} />
              0x LIMIT
            </div>
            <div className="flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded" style={{ backgroundColor: ratioToColor(0.5) }} />
              0.5x
            </div>
            <div className="flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded" style={{ backgroundColor: ratioToColor(1) }} />
              1x (LIMIT)
            </div>
            <div className="flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded" style={{ backgroundColor: ratioToColor(2) }} />
              2x+
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center gap-1">
              <span className="inline-block w-3 h-3 rounded bg-red-900/60 border border-red-700/40" />
              EXCEEDS {hasRegLimits ? 'ALL STANDARDS' : 'REG LIMIT'}
            </div>
            {hasRegLimits && (
              <div className="flex items-center gap-1">
                <span className="inline-block w-3 h-3 rounded bg-orange-900/50 border border-orange-700/40" />
                EXCEEDS SOME
              </div>
            )}
            <div className="flex items-center gap-1">
              <span className="inline-block w-3 h-3 rounded bg-emerald-900/50 border border-emerald-700/40" />
              BELOW {hasRegLimits ? 'ALL STANDARDS' : 'REG LIMIT'}
            </div>
          </>
        )}
        <div className="flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded bg-mono-800/60 border border-mono-700/40" />
          NON-DETECT
        </div>
        <div className="flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded bg-mono-900/20 border border-mono-800/20" />
          NO SAMPLE
        </div>
      </div>

      {/* Heatmap grid */}
      <div className="rounded-xl border overflow-x-auto" style={{ background: 'var(--dv-bg-card)', borderColor: 'var(--dv-border-subtle)' }}>
        {grid.length === 0 || dates.length === 0 ? (
          <div className="flex items-center justify-center py-16 text-mono-600 text-sm tracking-wider">
            SELECT WELLS TO DISPLAY
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b" style={{ borderColor: 'var(--dv-border-subtle)' }}>
                <th className="px-3 py-2 text-left text-[10px] tracking-wider font-medium sticky left-0 z-10 min-w-[100px]" style={{ color: 'var(--dv-text-muted)', background: 'var(--dv-bg-primary)' }}>
                  WELL
                </th>
                {dates.map(date => (
                  <th key={date} className="px-1 py-2 text-center text-[8px] text-mono-500 tracking-wider font-medium min-w-[36px]">
                    {(() => {
                      const d = new Date(date);
                      return `${d.getMonth() + 1}/${d.getFullYear().toString().slice(2)}`;
                    })()}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {grid.map((row, ri) => (
                <tr key={ri} className="border-b border-mono-800/30">
                  <td className="px-3 py-1.5 text-[10px] tracking-wider sticky left-0 z-10" style={{ color: 'var(--dv-text-primary)', background: 'var(--dv-bg-primary)' }}>
                    {row.well_name}
                  </td>
                  {row.cells.map((cell, ci) => {
                    const { className, style } = getCellStyle(cell);
                    const cellDate = dates[ci];
                    const opacity = getOpacity(row.well_name, cellDate);
                    return (
                      <td key={ci} className="px-0.5 py-0.5">
                        <div
                          className={className}
                          style={{ ...style, opacity, cursor: cell.status !== 'empty' ? 'pointer' : undefined }}
                          title={cell.value !== null && cell.value !== undefined ? `${cell.value}` : 'No sample'}
                          onClick={cell.status !== 'empty' ? () => highlight({ well: row.well_name, date: cellDate, analyte: activeAnalyte }) : undefined}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
